import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { MotionConfig } from 'motion/react';
import theme from './theme';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./pages/LandingPage/LandingPage'));
const Features = lazy(() => import('./pages/Features/Features'));
const Security = lazy(() => import('./pages/Security/Security'));
const DocumentScanning = lazy(() => import('./pages/DocumentScanning/DocumentScanning'));
const CloudBackup = lazy(() => import('./pages/CloudBackup/CloudBackup'));
const WearOS = lazy(() => import('./pages/WearOS/WearOS'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions/TermsAndConditions'));
const About = lazy(() => import('./pages/About/About'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

// Loading component
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <CircularProgress
      size={48}
      sx={{
        color: 'primary.main',
      }}
    />
  </Box>
);

// Scroll restoration component
const ScrollRestoration: React.FC = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return null;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <MotionConfig reducedMotion="user">
      <CssBaseline />
      <Router>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          <Box
            component="a"
            href="#main"
            sx={{
              position: 'absolute',
              left: 12,
              top: -60,
              zIndex: 2000,
              px: 2,
              py: 1,
              borderRadius: '10px',
              bgcolor: 'text.primary',
              color: '#fff',
              textDecoration: 'none',
              '&:focus': { top: 12 },
            }}
          >
            Skip to content
          </Box>
          <Navbar />
          <Box component="main" id="main" tabIndex={-1} sx={{ flex: 1, outline: 'none' }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<><ScrollRestoration /><LandingPage /></>} />
                <Route path="/features" element={<><ScrollRestoration /><Features /></>} />
                <Route path="/security" element={<><ScrollRestoration /><Security /></>} />
                <Route path="/document-scanning" element={<><ScrollRestoration /><DocumentScanning /></>} />
                <Route path="/cloud-backup" element={<><ScrollRestoration /><CloudBackup /></>} />
                <Route path="/wear-os" element={<><ScrollRestoration /><WearOS /></>} />
                <Route path="/privacy-policy" element={<><ScrollRestoration /><PrivacyPolicy /></>} />
                <Route path="/terms-and-conditions" element={<><ScrollRestoration /><TermsAndConditions /></>} />
                <Route path="/about" element={<><ScrollRestoration /><About /></>} />
                <Route path="*" element={<><ScrollRestoration /><NotFound /></>} />
              </Routes>
            </Suspense>
          </Box>
          <Footer />
          <ScrollToTop />
        </Box>
      </Router>
      </MotionConfig>
    </ThemeProvider>
  );
}

export default App;
